import React, { lazy, Suspense, useCallback, useState } from 'react';

import { useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import Checkbox from '@atlaskit/checkbox';
import { CheckboxField, Field } from '@atlaskit/form';
import Link from '@atlaskit/link';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import TextArea from '@atlaskit/textarea';

import type { FeedbackSubmitHandlerProps } from '../../../ai-answer-dialog/types';
import { FEEDBACK_CONTEXT_CF } from '../../../constants';

import { messages } from './messages';

const AkFeedbackCollector = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_@atlassian/product-search-dialog/feedback-collector" */ '@atlaskit/feedback-collector'
	).then((module) => {
		return {
			default: module.default,
		};
	}),
);

export enum FeedbackCollectorModalVariant {
	POSITIVE = 'positive',
	NEGATIVE = 'negative',
}

type FeedbackCollectorModalProps = {
	entrypointId: string;
	attributes: Record<string, string | null | undefined>;
	attributesWithConsent: Record<string, string>;
	userId?: string | undefined;
	userName?: string | undefined;
	onClose: () => void;
	onSubmit: (feedbackSubmitHandlerProps: Omit<FeedbackSubmitHandlerProps, 'liked'>) => void;
	variant: FeedbackCollectorModalVariant;
};

export const FeedbackCollectorModal = ({
	entrypointId,
	attributes,
	attributesWithConsent,
	userId,
	userName,
	onClose,
	onSubmit,
	variant,
}: FeedbackCollectorModalProps) => {
	const { formatMessage } = useIntl();
	const [hasConsent, setConsent] = useState(false);
	const [canContact, setCanContact] = useState(false);
	const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
	const [description, setDescription] = useState('');

	const POSITIVE_FEEDBACK_REASONS: string[] = [
		formatMessage(messages.ai_feedback_reason_up_to_date),
		formatMessage(messages.ai_feedback_reason_accurate),
		formatMessage(messages.ai_feedback_reason_helpful),
		formatMessage(messages.ai_feedback_reason_following_instructions),
		formatMessage(messages.ai_feedback_reason_good_sources),
		formatMessage(messages.ai_feedback_reason_other),
	];

	const NEGATIVE_FEEDBACK_REASONS: string[] = [
		formatMessage(messages.ai_feedback_reason_out_of_date),
		formatMessage(messages.ai_feedback_reason_inaccurate),
		formatMessage(messages.ai_feedback_reason_too_long),
		formatMessage(messages.ai_feedback_reason_too_short),
		formatMessage(messages.ai_feedback_reason_wrong_sources),
		formatMessage(messages.ai_feedback_reason_inappropriate),
		formatMessage(messages.ai_feedback_reason_other),
	];

	const feedbackOptions =
		variant === FeedbackCollectorModalVariant.POSITIVE
			? POSITIVE_FEEDBACK_REASONS
			: NEGATIVE_FEEDBACK_REASONS;

	const handleReasonClick = (option: string) => {
		setSelectedReasons((prev) =>
			prev.includes(option) ? prev.filter((r) => r !== option) : [...prev, option],
		);
	};

	const feedbackObject = {
		...attributes,
		...(hasConsent ? attributesWithConsent : {}),
		...(selectedReasons.length > 0 ? { 'Feedback Reasons': selectedReasons.join(', ') } : {}),
	};

	const feedbackContext = Object.entries(feedbackObject)
		.map(([key, value]) => `${key}: ${value}`)
		.join(', ');

	const toggleConsentCheckbox = useCallback(() => {
		setConsent(!hasConsent);
	}, [hasConsent]);

	const sendMetadata = {
		userId,
	};

	const getDescriptionWithMetadata = () => `
	${description}

	\`\`\`
	${JSON.stringify(sendMetadata, null, 2)}
	\`\`\`
  `;

	return (
		<Suspense fallback={<></>}>
			<AkFeedbackCollector
				onClose={onClose}
				onSubmit={() =>
					onSubmit({
						feedbackOptions: selectedReasons,
						additionalFeedbackAdded: description.length > 0,
						includeResponse: hasConsent,
						canContact: canContact,
					})
				}
				showTypeField={false}
				showDefaultTextFields={false}
				atlassianAccountId={userId}
				name={userName}
				entrypointId={entrypointId}
				additionalFields={[
					{
						id: FEEDBACK_CONTEXT_CF,
						value: feedbackContext,
					},
					{
						id: 'customfield_10043',
						value: canContact ? [{ id: '10109' }] : [{ id: '10111' }],
					},
				]}
				feedbackTitle={formatMessage(messages.ai_feedback_provide_additional_feedback_title)}
				descriptionDefaultValue={getDescriptionWithMetadata()}
				summaryDefaultValue={description}
				canBeContactedFieldId="undefined"
				showRequiredFieldsSummary={false}
				customContent={
					<Stack space="space.200">
						<Stack space="space.150">
							<Text weight="bold" color="color.text.subtle">
								{formatMessage(
									variant === FeedbackCollectorModalVariant.POSITIVE
										? messages.ai_feedback_what_has_gone_well_title
										: messages.ai_feedback_what_hasnt_gone_well_title,
								)}
							</Text>
							<Inline space="space.100" shouldWrap={true}>
								{feedbackOptions.map((option) => (
									<Button
										key={option}
										isSelected={selectedReasons.includes(option)}
										onClick={() => handleReasonClick(option)}
									>
										{option}
									</Button>
								))}
							</Inline>
						</Stack>
						<Stack space="space.100">
							<Field
								name="feedback-summary"
								label={formatMessage(messages.ai_feedback_additional_feedback_label)}
								isRequired={false}
								defaultValue=""
							>
								{({ fieldProps }) => (
									<TextArea
										{...fieldProps}
										name="feedback-collector-description"
										minimumRows={6}
										placeholder={formatMessage(
											messages.ai_feedback_additional_feedback_placeholder,
										)}
										onChange={(e) => setDescription(e.target.value)}
										value={description}
									/>
								)}
							</Field>
							{Object.keys(attributesWithConsent).length > 0 ? (
								<CheckboxField
									name="search-ai-include-prompt-and-response"
									defaultIsChecked={hasConsent}
								>
									{({ fieldProps }) => (
										<Checkbox
											{...fieldProps}
											label={formatMessage(messages.ai_feedback_report_consent_checkbox_label)}
											onChange={toggleConsentCheckbox}
										/>
									)}
								</CheckboxField>
							) : undefined}
							<CheckboxField name="can-be-contacted" defaultIsChecked={canContact}>
								{({ fieldProps }) => (
									<Checkbox
										{...fieldProps}
										label={
											formatMessage(messages.ai_feedback_can_contact_checkbox_label, {
												privatePolicyLink: (
													<Link
														href="https://www.atlassian.com/legal/privacy-policy"
														target="_blank"
													>
														{formatMessage(messages.ai_feedback_private_policy_link)}
													</Link>
												),
											}) as React.ReactChild
										}
										onChange={(event) => setCanContact(event.target.checked)}
									/>
								)}
							</CheckboxField>
						</Stack>
					</Stack>
				}
			/>
		</Suspense>
	);
};
